/**
 * Relative date-range presets, shared by BOTH sides of the AI filter pipeline
 * (same pattern as `sentiment-aspects`):
 *
 *  - apps/dash-meta's AI-search extraction has the model emit a PRESET TOKEN
 *    for named relative windows ("last year" → 'last-year') instead of
 *    guessing absolute dates (the model has no reliable notion of today, so
 *    guessed windows were stale or dropped — the 2026-08-25 demo's flaky
 *    "over the last year"), and its DateRangeResolver resolves the token
 *    against the data index's latest date;
 *  - the dashboard's manual date filter derives its preset dropdown from the
 *    SAME month offsets (datePresets.ts), and its AI-apply path RE-resolves a
 *    preset against the trial-lag-aware max so lagged accounts get a lagged
 *    window.
 *
 * The offsets are "months back from the anchor month", INCLUSIVE of the
 * anchor month — 'last-6-months' is anchor minus 5 months, exactly the
 * `subMonths(systemMaxDate, 5)` the manual preset has always used.
 */

export const RELATIVE_DATE_RANGE_PRESET_MONTHS_BACK = {
  'last-6-months': 5,
  'last-year': 11,
  'last-2-years': 23,
  'last-5-years': 59,
  'last-10-years': 119,
} as const;

export type RelativeDateRangePreset =
  keyof typeof RELATIVE_DATE_RANGE_PRESET_MONTHS_BACK;

/**
 * The dropdown's label per preset, shared with the assistant's narration so
 * both surfaces use the same words (dash-meta cannot reach a table in the
 * app). `satisfies` makes a token without a label a build failure.
 */
export const RELATIVE_DATE_RANGE_PRESET_UI_LABELS = {
  'last-6-months': 'Last 6 Months',
  'last-year': 'Last Year',
  'last-2-years': 'Last 2 Years',
  'last-5-years': 'Last 5 Years',
  'last-10-years': 'Last 10 Years',
} as const satisfies Record<RelativeDateRangePreset, string>;

/** The dropdown order, explicit rather than `Object.keys` so a tidy-up cannot reshuffle the UI. */
export const RELATIVE_DATE_RANGE_PRESETS_IN_UI_ORDER = [
  'last-6-months',
  'last-year',
  'last-2-years',
  'last-5-years',
  'last-10-years',
] as const satisfies readonly RelativeDateRangePreset[];

/**
 * How to SAY each preset in a sentence to the end user. Deliberately lower
 * case and spelled out — these appear mid-sentence in assistant narration
 * ("\"last 6 months\" was measured from …"), not as a dropdown label, and the
 * raw token ('last-6-months') must never reach the user: it is an internal
 * identifier, which the assistant's thinking instructions forbid surfacing
 * alongside field names and enum values.
 *
 * `satisfies Record<RelativeDateRangePreset, string>` is the point of putting
 * this beside the offsets rather than next to its one consumer: adding a
 * preset token above without a phrase here fails the build, so a new preset
 * cannot ship narrating itself as `last-3-years`.
 *
 * Derived from the dropdown labels (lower-cased) so a rename cannot leave the
 * narration naming a preset the UI no longer shows.
 */
export const RELATIVE_DATE_RANGE_PRESET_LABELS = Object.fromEntries(
  Object.entries(RELATIVE_DATE_RANGE_PRESET_UI_LABELS).map(([token, label]) => [
    token,
    label.toLowerCase(),
  ])
) as Record<RelativeDateRangePreset, string>;

export const isRelativeDateRangePreset = (
  value: unknown
): value is RelativeDateRangePreset =>
  typeof value === 'string' && value in RELATIVE_DATE_RANGE_PRESET_MONTHS_BACK;

const daysInMonth = (year: number, monthIndex0: number): number =>
  // Day 0 of the next month is the last day of this one (UTC-safe: these are
  // pure calendar-arithmetic Date instances, read back the same way).
  new Date(Date.UTC(year, monthIndex0 + 1, 0)).getUTCDate();

/**
 * Subtract whole months from an ISO `YYYY-MM-DD` day with end-of-month
 * clamping — the same semantics as date-fns `subMonths`, implemented without
 * the dependency so dash-meta can share it.
 */
export const subtractMonthsIso = (isoDay: string, months: number): string => {
  const match = isoDay.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return isoDay;
  const year = Number(match[1]);
  const monthIndex0 = Number(match[2]) - 1;
  const day = Number(match[3]);
  const total = year * 12 + monthIndex0 - months;
  const newYear = Math.floor(total / 12);
  const newMonthIndex0 = total - newYear * 12;
  const clampedDay = Math.min(day, daysInMonth(newYear, newMonthIndex0));
  return `${String(newYear).padStart(4, '0')}-${String(newMonthIndex0 + 1).padStart(2, '0')}-${String(clampedDay).padStart(2, '0')}`;
};

/**
 * Resolve a preset to full ISO bounds against a reference day (the data
 * index's latest available date). Returns `null` for an unknown preset or an
 * unusable reference.
 */
export const resolveRelativeDateRangePreset = (
  preset: string,
  referenceIsoDay: string
): { start_value: string; end_value: string } | null => {
  if (!isRelativeDateRangePreset(preset)) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(referenceIsoDay)) return null;
  return {
    start_value: subtractMonthsIso(
      referenceIsoDay,
      RELATIVE_DATE_RANGE_PRESET_MONTHS_BACK[preset]
    ),
    end_value: referenceIsoDay,
  };
};
